import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import type { ConfigStatus } from '../../types/onboarding.js';

const Schema = z.object({
  taxId: z.string().min(1),
  legalName: z.string().min(1).max(200),
  street: z.string().min(1),
  city: z.string().min(1),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().length(2),
  establishment: z.string().regex(/^\d{3}$/, 'Debe ser 3 dígitos (ej: 001)'),
  pos: z.string().regex(/^\d{3}$/, 'Debe ser 3 dígitos (ej: 001)'),
  sriEnvironment: z.enum(['1', '2']),
});
type FormData = z.infer<typeof Schema>;

export function FiscalData() {
  const { activeOrgId } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [taxIdError, setTaxIdError] = useState<string | null>(null);
  const { data: status } = useQuery({
    queryKey: ['config-status', activeOrgId],
    queryFn: () => api<ConfigStatus>(`/orgs/${activeOrgId}/config-status`),
    enabled: !!activeOrgId,
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(Schema),
    defaultValues: { country: 'EC', establishment: '001', pos: '001', sriEnvironment: '1' },
  });

  const seeded = useRef(false);
  useEffect(() => {
    if (!seeded.current && status?.organization.countryCode) {
      setValue('country', status.organization.countryCode);
      if (status.organization.establishment) {
        setValue('establishment', status.organization.establishment);
      }
      if (status.organization.pos) setValue('pos', status.organization.pos);
      if (status.organization.sriEnvironment) {
        setValue('sriEnvironment', status.organization.sriEnvironment);
      }
      seeded.current = true;
    }
  }, [status?.organization, setValue]);

  const submit = useMutation({
    mutationFn: (input: FormData) =>
      api(`/orgs/${activeOrgId}/config`, {
        method: 'PATCH',
        body: JSON.stringify({
          taxId: input.taxId,
          legalName: input.legalName,
          fiscalAddress: {
            street: input.street,
            city: input.city,
            state: input.state,
            postalCode: input.postalCode,
            country: input.country,
          },
          establishment: input.establishment,
          pos: input.pos,
          sriEnvironment: input.sriEnvironment,
        }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['config-status'] });
      nav('/app/onboarding/wizard/timezone');
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setTaxIdError(err.body?.error ?? err.message ?? 'Error desconocido');
    },
  });

  const onSubmit = handleSubmit((data) => submit.mutate(data));

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <h2 className="text-lg font-medium">Datos fiscales</h2>
      <p className="text-sm text-slate-600">
        Tax ID + razón social + dirección fiscal son requeridos para emitir comprobantes.
      </p>
      <label className="block">
        <span className="text-sm">Identificación fiscal (RUC/Cédula/NIT/...)</span>
        <input {...register('taxId')} className="mt-1 w-full rounded border px-3 py-2" />
        {errors.taxId && <span className="text-xs text-red-600">{errors.taxId.message}</span>}
        {taxIdError && <span className="text-xs text-red-600">{taxIdError}</span>}
      </label>
      <label className="block">
        <span className="text-sm">Razón social</span>
        <input {...register('legalName')} className="mt-1 w-full rounded border px-3 py-2" />
        {errors.legalName && (
          <span className="text-xs text-red-600">{errors.legalName.message}</span>
        )}
      </label>
      <fieldset className="space-y-3 rounded border p-3">
        <legend className="px-1 text-sm font-medium">Dirección fiscal</legend>
        <input
          {...register('street')}
          placeholder="Calle / dirección"
          className="w-full rounded border px-3 py-2"
        />
        {errors.street && <span className="text-xs text-red-600">{errors.street.message}</span>}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <input
              {...register('city')}
              placeholder="Ciudad"
              className="w-full rounded border px-3 py-2"
            />
            {errors.city && <span className="text-xs text-red-600">{errors.city.message}</span>}
          </div>
          <div>
            <input
              {...register('state')}
              placeholder="Provincia/Estado"
              className="w-full rounded border px-3 py-2"
            />
          </div>
          <div>
            <input
              {...register('postalCode')}
              placeholder="Código postal"
              className="w-full rounded border px-3 py-2"
            />
          </div>
          <div>
            <input
              {...register('country')}
              maxLength={2}
              placeholder="País (2 letras)"
              className="w-full rounded border px-3 py-2 uppercase"
            />
            {errors.country && (
              <span className="text-xs text-red-600">{errors.country.message}</span>
            )}
          </div>
        </div>
      </fieldset>
      <fieldset className="space-y-3 rounded border p-3">
        <legend className="px-1 text-sm font-medium">Configuración SRI (Ecuador)</legend>
        <p className="text-xs text-slate-600">
          Códigos del establecimiento y punto de emisión usados en la clave de acceso (49 dígitos)
          de comprobantes electrónicos.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm">Establecimiento (3 dígitos)</span>
            <input
              {...register('establishment')}
              maxLength={3}
              placeholder="001"
              className="mt-1 w-full rounded border px-3 py-2 font-mono"
            />
            {errors.establishment && (
              <span className="text-xs text-red-600">{errors.establishment.message}</span>
            )}
          </label>
          <label className="block">
            <span className="text-sm">Punto de emisión (3 dígitos)</span>
            <input
              {...register('pos')}
              maxLength={3}
              placeholder="001"
              className="mt-1 w-full rounded border px-3 py-2 font-mono"
            />
            {errors.pos && <span className="text-xs text-red-600">{errors.pos.message}</span>}
          </label>
        </div>
        <div>
          <span className="text-sm">Ambiente SRI</span>
          <div className="mt-1 flex gap-4 text-sm">
            <label className="inline-flex items-center gap-2">
              <input type="radio" value="1" {...register('sriEnvironment')} />
              <span>Pruebas</span>
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="radio" value="2" {...register('sriEnvironment')} />
              <span>Producción</span>
            </label>
          </div>
          {errors.sriEnvironment && (
            <span className="text-xs text-red-600">{errors.sriEnvironment.message}</span>
          )}
        </div>
      </fieldset>
      <div className="flex justify-end gap-3 pt-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
        >
          Guardar y continuar →
        </button>
      </div>
    </form>
  );
}
